import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { api, ApiError, InvitePreview } from "../lib/api";

// Public-readable invite landing. Renders a preview of the invite (who
// invited, what role, which session if applicable) BEFORE forcing the
// user through OAuth — no surprise sign-ins. Once the user clicks
// "Sign in to accept", we navigate to /admin: the host-wide oauth2-proxy
// redirects to OAuth, and OrgUserProvider auto-claims the invite on the
// first authenticated request after sign-in completes.
export default function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.getInvite(token);
        if (!cancelled) setPreview(r);
      } catch (e) {
        if (!cancelled) {
          setErr(
            e instanceof ApiError
              ? e.message
              : "could not load invite",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (err) {
    return (
      <main>
        <h1>Invite not available</h1>
        <p className="error" role="alert">
          {err}
        </p>
      </main>
    );
  }

  if (preview === null) {
    return (
      <main>
        <p className="meta">Loading invite…</p>
      </main>
    );
  }

  if (preview.accepted) {
    return (
      <main>
        <h1>Already accepted</h1>
        <p className="meta">
          This invite has already been accepted. If that wasn't you, ask
          {" "}
          <strong>{preview.inviter_email}</strong> for a fresh one.
        </p>
      </main>
    );
  }

  if (new Date(preview.expires_at).getTime() < Date.now()) {
    return (
      <main>
        <h1>Invite expired</h1>
        <p className="meta">
          This invite has expired. Ask{" "}
          <strong>{preview.inviter_email}</strong> for a fresh one.
        </p>
      </main>
    );
  }

  return (
    <main>
      <div className="reading">
        <h1>You're invited</h1>
        <p>
          <strong>{preview.inviter_email}</strong> has invited{" "}
          <strong>{preview.email}</strong> to join AskTogether as{" "}
          <strong>{preview.role}</strong>
          {preview.session_name ? (
            <>
              {" "}for the session <strong>{preview.session_name}</strong>
            </>
          ) : null}
          .
        </p>
        <p className="meta">
          {preview.role === "operator"
            ? "Operators create and run sessions. The admin who invited you will see your sessions in their dashboard."
            : "Moderators handle question approval and answering for one specific session."}
        </p>
        <p className="meta">
          Expires {new Date(preview.expires_at).toLocaleString()}.
        </p>
        <p style={{ marginTop: "1.5rem" }}>
          <a
            href="/admin"
            style={{
              display: "inline-block",
              padding: "0.6rem 1rem",
              background: "var(--accent)",
              color: "var(--accent-fg)",
              borderRadius: "6px",
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            Sign in to accept
          </a>
        </p>
        <p className="meta" style={{ fontSize: "0.85rem" }}>
          You'll be redirected through Google sign-in. The invite is bound
          to <strong>{preview.email}</strong>; sign in with that exact
          address.
        </p>
      </div>
    </main>
  );
}
